/**
 * Word (.docx) import pipeline for LexGat.
 *
 * Flow: .docx → mammoth → OOXML paragraph supplement → sanitize → normalize → editor HTML
 *
 * Fidelity phases: docs/DOCX_FIDELITY_PHASES.md
 * Phase 2 numbering fallback: docxImport.phase2.md
 */
import mammoth from "mammoth";
import {
  applyLegalParagraphHeuristics,
  convertDocxWithParagraphFidelity,
  extractAltChunkContent,
  normalizeImportedInlineHtml,
  type LegalHeuristicsStats,
  type NumberingFidelityStats,
  type ParagraphFidelityStats,
  type TableFidelityStats,
  type TextFidelityStats,
} from "./docxFidelity";
import { prepareTableForEditorResize, refreshTableCellAlignments } from "./docxFidelity/merge/prepareTableForEditor";
import { markImportedOrderedListsInRoot } from "./editor/listNumbering";
import { parseCssLengthToPx } from "./docxFidelity/units";
import {
  buildImportFidelityReport as composeFidelityReport,
  detectComplexNumbering,
  type ImportFidelityReport,
} from "./docxFidelity/report/buildFidelityReport";
import {
  DEFAULT_LINE_SPACING,
  DEFAULT_PARAGRAPH_SPACING_PT,
} from "./editor-defaults";
import {
  analyzeLegalDocumentSplits,
  type DocxSplitAnalysis,
  type LegalDocumentSegment,
} from "./docxSplit/detectLegalDocumentSplits";

export type ImportMode = "preserve" | "lexgat";

export type ImportQualityTier = "excellent" | "good" | "review_recommended";

export interface ImportWarning {
  code: string;
  message: string;
}

export type { ImportFidelityReport } from "./docxFidelity/report/buildFidelityReport";
export { detectComplexNumbering };

export interface NormalizedImportResult {
  content: string;
  warnings: ImportWarning[];
  fidelity: ImportFidelityReport;
}

export interface ImportDocxOptions {
  mode?: ImportMode;
}

export interface ImportDocxResult extends NormalizedImportResult {
  plainText: string;
  splitAnalysis: DocxSplitAnalysis;
}

export type { DocxSplitAnalysis, LegalDocumentSegment } from "./docxSplit/detectLegalDocumentSplits";

export type DocxImportLayout = "tabs" | "single";

export interface ImportTabDraft {
  id: string;
  title: string;
  content: string;
}

/** Build editor/storage tabs from an import result and layout choice. */
export function buildImportTabs(
  result: ImportDocxResult,
  layout: DocxImportLayout,
): ImportTabDraft[] {
  if (layout === "tabs" && result.splitAnalysis.canSplit) {
    const stamp = Date.now();
    return result.splitAnalysis.segments.map((seg, i) => ({
      id: `tab-${stamp}-${i}`,
      title: seg.title,
      content: seg.content,
    }));
  }

  return [
    {
      id: `tab-${Date.now()}`,
      title: "Document 1",
      content: result.content,
    },
  ];
}

const MAX_DOCX_BYTES = 25 * 1024 * 1024;

const BLOCK_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "TD",
  "TH",
  "BLOCKQUOTE",
]);

const JUNK_TAG_NAMES = new Set([
  "O:P",
  "W:SDT",
  "V:SHAPE",
  "V:SHAPETYPE",
  "V:TEXTBOX",
  "W:SMARTTAG",
]);

function stripMsoStyles(style: string): string {
  return style
    .split(";")
    .filter((part) => {
      const key = part.split(":")[0]?.trim().toLowerCase() ?? "";
      return key && !key.startsWith("mso-");
    })
    .join(";");
}

function isExplicitPageBreakStyle(style: string): boolean {
  const lower = style.toLowerCase();
  return (
    /page-break-before\s*:\s*(always|page)\b/.test(lower) ||
    /page-break-after\s*:\s*(always|page)\b/.test(lower) ||
    /break-before\s*:\s*page\b/.test(lower) ||
    /break-after\s*:\s*page\b/.test(lower)
  );
}

function isEmptyBlock(el: Element): boolean {
  if (el instanceof HTMLElement) {
    if (el.classList.contains("page-break")) return false;
    const style = el.getAttribute("style") ?? "";
    if (isExplicitPageBreakStyle(style)) {
      return false;
    }
  }
  const text = (el.textContent ?? "").replace(/\u00a0/g, " ").trim();
  if (text) return false;
  return !el.querySelector("img, table, br, hr, div.page-break");
}

function unwrapRedundantSpans(root: ParentNode): void {
  const spans = root.querySelectorAll("span");
  spans.forEach((span) => {
    if (span.attributes.length === 0 && span.parentElement) {
      const parent = span.parentElement;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      parent.removeChild(span);
    }
  });
}

export function validateDocxFile(file: File): ImportWarning | null {
  const name = file.name.toLowerCase();
  if (!name.endsWith(".docx")) {
    return {
      code: "invalid_type",
      message: "Only .docx files are supported. Save your document as Word (.docx) and try again.",
    };
  }
  if (file.size > MAX_DOCX_BYTES) {
    return {
      code: "file_too_large",
      message: `File exceeds ${Math.round(MAX_DOCX_BYTES / 1024 / 1024)} MB limit.`,
    };
  }
  return null;
}

export async function convertDocxToHtml(
  arrayBuffer: ArrayBuffer,
): Promise<{
  html: string;
  mammothMessages: ImportWarning[];
  paragraphStats?: ParagraphFidelityStats;
  numberingStats?: NumberingFidelityStats;
  tableStats?: TableFidelityStats;
  textStats?: TextFidelityStats;
}> {
  const result = await convertDocxWithParagraphFidelity(arrayBuffer);
  return {
    html: result.html,
    mammothMessages: result.mammothMessages,
    paragraphStats: result.paragraphStats,
    numberingStats: result.numberingStats,
    tableStats: result.tableStats,
    textStats: result.textStats,
  };
}

export async function extractDocxPlainText(
  arrayBuffer: ArrayBuffer,
): Promise<string> {
  const mammothInput: { arrayBuffer: ArrayBuffer; buffer?: Buffer } = {
    arrayBuffer,
  };
  if (typeof Buffer !== "undefined") {
    mammothInput.buffer = Buffer.from(arrayBuffer);
  }

  const result = await mammoth.extractRawText(mammothInput);
  if (result.value.trim()) return result.value;

  const altChunk = await extractAltChunkContent(arrayBuffer).catch(() => null);
  return altChunk?.plainText ?? "";
}

export function sanitizeWordHtmlRoot(
  root: HTMLElement,
  inlineOptions?: Parameters<typeof normalizeImportedInlineHtml>[1],
): void {
  root.querySelectorAll("*").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    if (JUNK_TAG_NAMES.has(node.tagName)) {
      node.remove();
      return;
    }

    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (
        name.startsWith("xmlns") ||
        name.startsWith("o:") ||
        name.startsWith("w:") ||
        name.startsWith("v:") ||
        name.startsWith("mso-") ||
        name === "class" && /mso/i.test(attr.value)
      ) {
        node.removeAttribute(attr.name);
      }
    });

    if (node.hasAttribute("style")) {
      const cleaned = stripMsoStyles(node.getAttribute("style") ?? "");
      if (cleaned.trim()) node.setAttribute("style", cleaned);
      else node.removeAttribute("style");
    }

    if (node.className && typeof node.className === "string") {
      const classes = node.className
        .split(/\s+/)
        .filter((c) => c && !/^mso/i.test(c));
      if (classes.length) node.className = classes.join(" ");
      else node.removeAttribute("class");
    }
  });

  root.querySelectorAll("p, div").forEach((el) => {
    if (isEmptyBlock(el)) el.remove();
  });

  unwrapRedundantSpans(root);
  normalizeImportedInlineHtml(root, inlineOptions);
}

export function sanitizeWordHtml(html: string): string {
  if (!html.trim()) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="import-root">${html}</div>`, "text/html");
  const root = doc.getElementById("import-root");
  if (!root) return html;

  sanitizeWordHtmlRoot(root);
  return root.innerHTML.trim();
}

function applyAlignmentFromClass(el: HTMLElement): void {
  const classes = (el.className || "").toLowerCase();
  if (classes.includes("center") || classes.includes("text-center")) {
    el.style.textAlign = "center";
  } else if (classes.includes("right") || classes.includes("text-right")) {
    el.style.textAlign = "right";
  } else if (classes.includes("justify") || classes.includes("text-justify")) {
    el.style.textAlign = "justify";
  } else if (classes.includes("left") || classes.includes("text-left")) {
    el.style.textAlign = "left";
  }
}

export function mapParagraphSpacingStyles(el: HTMLElement): void {
  const sides = ["marginLeft", "marginRight", "textIndent"] as const;
  for (const prop of sides) {
    const px = parseCssLengthToPx(el.style[prop]);
    if (px != null && px !== 0) el.style[prop] = `${px}px`;
  }
  for (const prop of ["marginTop", "marginBottom"] as const) {
    const px = parseCssLengthToPx(el.style[prop]);
    if (px != null) el.style[prop] = `${px}px`;
  }
}

function hasMeaningfulText(el: HTMLElement): boolean {
  return Boolean(
    (el.textContent ?? "").replace(/\u00a0/g, " ").replace(/\f/g, "").trim(),
  );
}

function hasPageBreakStyle(el: HTMLElement): boolean {
  return isExplicitPageBreakStyle(el.getAttribute("style") ?? "");
}

export { isExplicitPageBreakStyle };

function createPageBreakDiv(doc: Document): HTMLDivElement {
  const br = doc.createElement("div");
  br.className = "page-break";
  br.setAttribute("style", "page-break-after: always; break-after: page;");
  return br;
}

function stripPageBreakStyles(el: HTMLElement): void {
  const style = el.getAttribute("style") ?? "";
  const cleaned = style
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const key = part.split(":")[0]?.trim().toLowerCase() ?? "";
      return (
        key !== "page-break-before" &&
        key !== "page-break-after" &&
        key !== "break-before" &&
        key !== "break-after"
      );
    })
    .join("; ");
  if (cleaned) el.setAttribute("style", cleaned);
  else el.removeAttribute("style");
}

function mapFormFeedBreaks(root: ParentNode): void {
  const doc =
    root instanceof Document
      ? root
      : root.ownerDocument ?? (root as ParentNode & { ownerDocument?: Document }).ownerDocument;
  if (!doc) return;

  const textNodes: Text[] = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.textContent?.includes("\f")) {
      textNodes.push(node as Text);
    }
  }

  for (const textNode of textNodes) {
    const parts = textNode.textContent!.split("\f");
    const fragment = doc.createDocumentFragment();
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) {
        fragment.appendChild(createPageBreakDiv(doc));
      }
      if (parts[i]) {
        fragment.appendChild(doc.createTextNode(parts[i]));
      }
    }
    textNode.parentNode?.replaceChild(fragment, textNode);
  }
}

function mapStyledPageBreaks(root: ParentNode): void {
  const nodes = Array.from(root.querySelectorAll("*")).filter(
    (node): node is HTMLElement => node instanceof HTMLElement,
  );

  for (const node of nodes) {
    if (!hasPageBreakStyle(node) || node.classList.contains("page-break")) {
      continue;
    }

    if (!hasMeaningfulText(node)) {
      const br = createPageBreakDiv(node.ownerDocument);
      node.parentElement?.replaceChild(br, node);
      continue;
    }

    const style = (node.getAttribute("style") ?? "").toLowerCase();
    const hasBefore =
      style.includes("page-break-before") ||
      style.includes("break-before: page");
    const hasAfter =
      style.includes("page-break-after") ||
      style.includes("break-after: page");

    if (hasBefore) {
      node.parentElement?.insertBefore(
        createPageBreakDiv(node.ownerDocument),
        node,
      );
    }
    stripPageBreakStyles(node);
    if (hasAfter) {
      node.parentElement?.insertBefore(
        createPageBreakDiv(node.ownerDocument),
        node.nextSibling,
      );
    }
  }
}

function mapWordSectionBreaks(root: ParentNode): void {
  const sections = Array.from(root.querySelectorAll("*")).filter(
    (node): node is HTMLElement =>
      node instanceof HTMLElement &&
      node.className
        .split(/\s+/)
        .some((cls) => /^WordSection\d+$/i.test(cls)),
  );

  for (let i = 1; i < sections.length; i++) {
    const section = sections[i];
    const previous = section.previousElementSibling;
    if (previous?.classList.contains("page-break")) continue;
    if (
      previous instanceof HTMLElement &&
      hasPageBreakStyle(previous) &&
      !hasMeaningfulText(previous)
    ) {
      continue;
    }
    section.parentElement?.insertBefore(
      createPageBreakDiv(section.ownerDocument),
      section,
    );
  }
}

function collectFlatBlocks(root: ParentNode): HTMLElement[] {
  const blocks: HTMLElement[] = [];

  function walk(parent: Node): void {
    for (const node of Array.from(parent.childNodes)) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.classList.contains("page-break")) {
        blocks.push(node);
        continue;
      }

      if (
        node.tagName === "DIV" &&
        !node.className.split(/\s+/).some((cls) => /^WordSection\d+$/i.test(cls))
      ) {
        walk(node);
        continue;
      }

      blocks.push(node);
    }
  }

  walk(root);
  return blocks;
}

function isEmptyBreakPlaceholder(el: HTMLElement): boolean {
  if (el.classList.contains("page-break") || hasPageBreakStyle(el)) return true;
  if (hasMeaningfulText(el)) return false;
  return !el.querySelector("img, table, hr, br, ul, ol, blockquote");
}

function mapSandwichedEmptyBlockBreaks(root: ParentNode): void {
  const blocks = collectFlatBlocks(root);

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block.classList.contains("page-break")) continue;
    if (!isEmptyBreakPlaceholder(block)) continue;

    const prev = blocks[i - 1];
    const next = blocks[i + 1];
    if (
      !prev ||
      !next ||
      isEmptyBreakPlaceholder(prev) ||
      isEmptyBreakPlaceholder(next) ||
      !hasMeaningfulText(prev) ||
      !hasMeaningfulText(next)
    ) {
      continue;
    }

    const br = createPageBreakDiv(block.ownerDocument);
    block.parentElement?.replaceChild(br, block);
    blocks[i] = br;
  }
}

function promoteInlinePageBreakBrTags(root: ParentNode): void {
  const brNodes = Array.from(root.querySelectorAll("br")).filter(
    (node): node is HTMLBRElement =>
      node instanceof HTMLBRElement && hasPageBreakStyle(node),
  );

  for (const br of brNodes) {
    const doc = br.ownerDocument;
    const pageBreak = createPageBreakDiv(doc);
    const parent = br.parentElement;
    if (!parent) {
      br.replaceWith(pageBreak);
      continue;
    }

    const isContainerRoot =
      parent.id === "paste-root" ||
      parent.id === "split-root" ||
      parent.id === "import-root" ||
      parent.id === "norm-root";

    const splittableParent =
      !isContainerRoot &&
      (parent.tagName === "P" ||
        parent.tagName === "LI" ||
        /^H[1-6]$/.test(parent.tagName) ||
        (parent.tagName === "DIV" && parent.childNodes.length > 1));

    if (!splittableParent) {
      br.replaceWith(pageBreak);
      continue;
    }

    const afterNodes: Node[] = [];
    let found = false;
    for (const child of Array.from(parent.childNodes)) {
      if (child === br) {
        found = true;
        continue;
      }
      if (found) afterNodes.push(child);
    }

    br.remove();
    for (const node of afterNodes) {
      node.remove();
    }

    parent.after(pageBreak);
    if (afterNodes.length > 0) {
      const afterTag = parent.tagName === "DIV" ? "div" : "p";
      const afterEl = doc.createElement(afterTag);
      for (const node of afterNodes) {
        afterEl.appendChild(node);
      }
      pageBreak.after(afterEl);
    }
  }
}

export type MapPageBreaksOptions = {
  /** When true, treat mammoth empty paragraphs / Word sections as breaks (docx import). */
  importHeuristics?: boolean;
};

export function mapPageBreaks(
  root: ParentNode,
  options: MapPageBreaksOptions = {},
): void {
  const importHeuristics = options.importHeuristics ?? false;
  mapFormFeedBreaks(root);
  promoteInlinePageBreakBrTags(root);
  mapStyledPageBreaks(root);
  if (importHeuristics) {
    mapWordSectionBreaks(root);
    mapSandwichedEmptyBlockBreaks(root);
  }
}

export function finalizeImportedTables(root: ParentNode): void {
  root.querySelectorAll("table").forEach((table) => {
    if (!(table instanceof HTMLTableElement)) return;
    table.classList.add("lexgat-import-table");
    if (!table.style.borderCollapse) {
      table.style.borderCollapse = "collapse";
    }
    prepareTableForEditorResize(table);
  });
}

export function applyLexGatFormattingToHtml(html: string): string {
  let cleanHtml = html
    .replace(/font-family\s*:\s*[^;"]+;?/gi, "")
    .replace(/font-size\s*:\s*[^;"]+;?/gi, "")
    .replace(/line-height\s*:\s*[^;"]+;?/gi, "")
    .replace(/color\s*:\s*[^;"]+;?/gi, "")
    .replace(/background-color\s*:\s*[^;"]+;?/gi, "")
    .replace(/margin-top\s*:\s*[^;"]+;?/gi, "")
    .replace(/margin-bottom\s*:\s*[^;"]+;?/gi, "");

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHtml, "text/html");
  doc.body.querySelectorAll("*").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.fontFamily = "Times New Roman";
    node.style.fontSize = "13pt";
    const tag = node.tagName.toUpperCase();
    if (BLOCK_TAGS.has(tag)) {
      node.style.lineHeight = DEFAULT_LINE_SPACING;
      node.style.marginTop = "0pt";
      node.style.marginBottom = `${DEFAULT_PARAGRAPH_SPACING_PT}pt`;
    }
  });
  return doc.body.innerHTML;
}

export function normalizeImportedDocument(options: {
  html: string;
  mode: ImportMode;
  mammothMessages?: ImportWarning[];
  paragraphStats?: ParagraphFidelityStats;
  numberingStats?: NumberingFidelityStats;
  tableStats?: TableFidelityStats;
  textStats?: TextFidelityStats;
}): NormalizedImportResult {
  const {
    html,
    mode,
    mammothMessages = [],
    paragraphStats,
    numberingStats,
    tableStats,
    textStats,
  } = options;
  const warnings: ImportWarning[] = [...mammothMessages];

  if (!html.trim()) {
    return {
      content: "",
      warnings,
      fidelity: {
        qualityTier: "review_recommended",
        estimatedPreservationPercent: 0,
        issues: ["The document appears to be empty after conversion."],
        unsupportedFeatures: [],
        preserved: [],
        reviewRecommended: [],
        summary: "Review recommended — the document appears empty after conversion.",
      },
    };
  }

  let processed = sanitizeWordHtml(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="norm-root">${processed}</div>`, "text/html");
  const root = doc.getElementById("norm-root");
  if (!root) {
    return {
      content: processed,
      warnings,
      fidelity: composeFidelityReport({
        html: processed,
        warnings,
        mammothMessages,
        paragraphStats,
        numberingStats,
        tableStats,
        textStats,
        legalHeuristicsStats,
      }),
    };
  }

  mapPageBreaks(root, { importHeuristics: true });
  finalizeImportedTables(root);
  markImportedOrderedListsInRoot(root);

  let legalHeuristicsStats: LegalHeuristicsStats | undefined;

  root.querySelectorAll("p, h1, h2, h3, h4, h5, h6, li, blockquote").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest("table")) return;
    const existingAlign = el.style.textAlign?.toLowerCase();
    applyAlignmentFromClass(el);
    if (!el.style.textAlign && existingAlign) {
      el.style.textAlign = existingAlign;
    }
    if (!el.style.textAlign) {
      const align = el.getAttribute("align");
      if (align) el.style.textAlign = align.toLowerCase();
    }
    mapParagraphSpacingStyles(el);
  });

  if (mode === "preserve") {
    legalHeuristicsStats = applyLegalParagraphHeuristics(root);
    if (legalHeuristicsStats.totalApplied > 0) {
      warnings.push({
        code: "legal_layout_heuristics",
        message: "Common legal layout patterns were preserved from pleading structure.",
      });
    }
  }

  refreshTableCellAlignments(root);

  processed = root.innerHTML;

  if (mode === "lexgat") {
    processed = applyLexGatFormattingToHtml(processed);
  }

  const unsupportedFeatures = [
    "Headers and footers",
    "Footnotes and endnotes",
    "Comments and track changes",
    "Text boxes and floating objects",
  ];

  warnings.push({
    code: "headers_footers",
    message: "Headers and footers were not imported.",
  });

  const numberingEnriched = (numberingStats?.numbersApplied ?? 0) > 0;

  if (detectComplexNumbering(processed) && !numberingEnriched) {
    warnings.push({
      code: "complex_numbering",
      message:
        "Complex legal numbering may need manual review after import.",
    });
  } else if (numberingEnriched && (numberingStats?.multilevelApplied ?? 0) > 0) {
    warnings.push({
      code: "numbering_imported",
      message: "Multilevel legal numbering was preserved from Word.",
    });
  }

  const fidelity = composeFidelityReport({
    html: processed,
    warnings,
    mammothMessages,
    paragraphStats,
    numberingStats,
    tableStats,
    textStats,
    legalHeuristicsStats,
  });

  return { content: processed, warnings, fidelity };
}

/** @deprecated Prefer composeFidelityReport object input — kept for tests and callers */
export function buildImportFidelityReport(
  html: string,
  warnings: ImportWarning[],
  mammothMessages: ImportWarning[] = [],
  paragraphStats?: ParagraphFidelityStats,
  numberingStats?: NumberingFidelityStats,
  tableStats?: TableFidelityStats,
  textStats?: TextFidelityStats,
  legalHeuristicsStats?: LegalHeuristicsStats,
): ImportFidelityReport {
  return composeFidelityReport({
    html,
    warnings,
    mammothMessages,
    paragraphStats,
    numberingStats,
    tableStats,
    textStats,
    legalHeuristicsStats,
  });
}

export async function importDocxFile(
  file: File,
  options: ImportDocxOptions = {},
): Promise<ImportDocxResult> {
  const validationError = validateDocxFile(file);
  if (validationError) {
    throw new Error(validationError.message);
  }

  const mode = options.mode ?? "preserve";
  const arrayBuffer = await file.arrayBuffer();
  const { html, mammothMessages, paragraphStats, numberingStats, tableStats, textStats } =
    await convertDocxToHtml(arrayBuffer);
  const plainText = await extractDocxPlainText(arrayBuffer);

  if (!html.trim() && !plainText.trim()) {
    throw new Error(
      "The document appears to be empty or contains no readable text.",
    );
  }

  const normalized = normalizeImportedDocument({
    html,
    mode,
    mammothMessages,
    paragraphStats,
    numberingStats,
    tableStats,
    textStats,
  });

  const splitAnalysis = analyzeLegalDocumentSplits(normalized.content);

  return {
    ...normalized,
    plainText,
    splitAnalysis,
  };
}
